import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { Link } from 'react-router-dom';

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '磁力猫',
  url: 'https://www.ciligod.com',
  description: '磁力猫是专业的磁力链接搜索引擎，提供海量影视、音乐、软件、电子书等资源的磁力链接免费搜索与下载服务。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@ciligod.com',
    availableLanguage: ['Chinese'],
  },
};

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于磁力猫',
  description: '了解磁力猫的品牌故事、核心优势、用户口碑和联系方式。',
  url: 'https://www.ciligod.com/about',
};

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="关于我们"
        description="了解磁力猫的品牌故事、核心优势、用户口碑和联系方式。磁力猫是专业的磁力链接搜索引擎，提供海量资源的免费搜索与下载服务。"
        keywords="磁力猫,关于磁力猫,磁力猫品牌,磁力搜索引擎介绍,磁力链接搜索"
        canonicalUrl="https://www.ciligod.com/about"
        ogUrl="https://www.ciligod.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />

      <div className="min-h-screen bg-background-50 pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <section className="mt-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              关于磁力猫
            </h1>
            <p className="text-sm text-foreground-500 mb-12 max-w-2xl">
              专业的磁力链接搜索引擎，致力于为用户提供最便捷的资源搜索体验
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    磁力猫成立于2020年，是一支由技术极客和产品设计师组成的团队。我们深知在互联网上寻找优质数字资源的痛点——信息碎片化、链接失效、下载速度慢。于是，我们创建了磁力猫，一个专注于磁力链接搜索的垂直搜索引擎。
                  </p>
                  <p>
                    磁力猫的核心理念是"海量资源，一搜即达"。我们通过先进的分布式爬虫技术和智能索引算法，聚合了来自全球的优质磁力链接资源，覆盖影视、音乐、软件、电子书、游戏、动漫、纪录片、学习资料等八大领域。
                  </p>
                  <p>
                    经过多年的技术积累和用户反馈优化，磁力猫已经成为国内最受欢迎的磁力链接搜索引擎之一，日均服务超过100万用户，资源库总量超过50万条。
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-8 flex flex-col justify-center">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: '50万+', label: '资源总量', icon: 'ri-database-2-line' },
                    { value: '100万+', label: '日活用户', icon: 'ri-user-smile-line' },
                    { value: '99.9%', label: '搜索成功率', icon: 'ri-check-double-line' },
                    { value: '5年+', label: '稳定运营', icon: 'ri-shield-check-line' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-xl bg-primary-100 text-primary-600 text-lg mb-2">
                        <i className={stat.icon}></i>
                      </span>
                      <div className="text-xl font-heading font-bold text-foreground-950">{stat.value}</div>
                      <div className="text-xs text-foreground-400 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  icon: 'ri-search-eye-line',
                  title: '极速搜索',
                  desc: '毫秒级响应速度，精准匹配你想要的资源，告别漫长的搜索等待。',
                  color: 'primary',
                },
                {
                  icon: 'ri-database-2-line',
                  title: '海量资源',
                  desc: '50万+磁力链接资源库，覆盖八大热门分类，而且每天都在持续增长。',
                  color: 'accent',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '安全可靠',
                  desc: '智能过滤无效和低质量链接，确保搜索结果的质量和可用性。',
                  color: 'secondary',
                },
                {
                  icon: 'ri-user-smile-line',
                  title: '完全免费',
                  desc: '无需注册、无需付费，所有搜索功能完全免费开放使用。',
                  color: 'primary',
                },
              ].map((item) => {
                const colorMap: Record<string, { bg: string; iconBg: string; iconColor: string }> = {
                  primary: { bg: 'bg-primary-50', iconBg: 'bg-primary-100', iconColor: 'text-primary-600' },
                  accent: { bg: 'bg-accent-50', iconBg: 'bg-accent-100', iconColor: 'text-accent-600' },
                  secondary: { bg: 'bg-secondary-50', iconBg: 'bg-secondary-100', iconColor: 'text-secondary-600' },
                };
                const c = colorMap[item.color];
                return (
                  <div key={item.title} className={`${c.bg} rounded-xl p-5`}>
                    <span className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.iconBg} ${c.iconColor} text-lg mb-3`}>
                      <i className={item.icon}></i>
                    </span>
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: '张同学',
                  role: '影视爱好者',
                  content: '磁力猫真的太方便了！找高清电影资源一搜就有，做种人数多、下载速度快，已经推荐给身边所有朋友了。',
                  rating: 5,
                },
                {
                  name: '李工程师',
                  role: '软件开发工程师',
                  content: '作为开发者，经常需要下载各种开发工具和镜像文件，磁力猫的软件分类非常实用，分类清晰、资源齐全。',
                  rating: 5,
                },
                {
                  name: '王老师',
                  role: '大学讲师',
                  content: '找学习资料和公开课资源的神器！课程资源丰富，特别适合学生和自学者使用，强烈推荐。',
                  rating: 5,
                },
              ].map((review) => (
                <div key={review.name} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-semibold">
                      {review.name[0]}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
            <div className="bg-background-100 rounded-2xl p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <span className="w-12 h-12 flex items-center justify-center mx-auto rounded-xl bg-primary-100 text-primary-600 text-xl mb-3">
                  <i className="ri-mail-line"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1">电子邮件</h3>
                <p className="text-xs text-foreground-500">support@ciligod.com</p>
                <p className="text-xs text-foreground-400 mt-1">工作日24小时内回复</p>
              </div>
              <div className="text-center">
                <span className="w-12 h-12 flex items-center justify-center mx-auto rounded-xl bg-accent-100 text-accent-600 text-xl mb-3">
                  <i className="ri-question-answer-line"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1">意见反馈</h3>
                <p className="text-xs text-foreground-500">feedback@ciligod.com</p>
                <p className="text-xs text-foreground-400 mt-1">欢迎提出宝贵建议</p>
              </div>
              <div className="text-center">
                <span className="w-12 h-12 flex items-center justify-center mx-auto rounded-xl bg-secondary-100 text-secondary-600 text-xl mb-3">
                  <i className="ri-share-line"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1">社交媒体</h3>
                <div className="flex items-center justify-center gap-3 mt-2">
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博">
                    <i className="ri-weibo-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微信">
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer" aria-label="GitHub">
                    <i className="ri-github-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <div className="bg-secondary-50 rounded-2xl p-8 text-center">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                开始你的探索之旅
              </h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                立即体验磁力猫强大的搜索功能，海量资源等你发现
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页搜索
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}